import {Entry, ThemePreference} from './types';
import {TimelineScale} from './utils/timelineUtils';

const ENTRIES_STORAGE_KEY='entries';
const THEME_MODE_KEY='themeMode';
const IS_INTRO_DIALOG_COMPLETED_KEY = 'isIntroDialogCompleted';
const IS_ABOUT_ONBOARDING_COMPLETED_KEY = 'isAboutOnboardingCompleted';
const IS_OFFLINE_SYNC_PROMPT_DISMISSED_KEY = 'isOfflineSyncPromptDismissed';
const IS_OFFLINE_CONTINUED_KEY = 'isOfflineContinued';
/** @deprecated Removed from writes; still removed on reset until all clients have migrated. */
const LEGACY_IS_COOKIE_PROMPT_ACCEPTED_KEY = 'isCookiePromptAccepted';
export const COOKIE_PREFERENCES_V1_STORAGE_KEY = 'cookiePreferencesV1';

export type CookiePreferencesV1 = {
	completed: boolean;
	analytics: boolean;
	/**
	 * False when the user only used “Reject all” (essential-only shortcut).
	 * Intro onboarding stays blocked until they use “Save preferences”.
	 * Omitted in stored JSON for legacy rows → treated as true for migration.
	 */
	cookieChoiceConfirmedWithSave?: boolean;
};

export const storage = window.localStorage;
export const sessionStorageStore = window.sessionStorage;

export const storeLocalEntries = (entries: Entry[]) => {
	console.debug('updating local storage', ENTRIES_STORAGE_KEY);
	storage.setItem(ENTRIES_STORAGE_KEY, JSON.stringify(entries));
};

export const obtainLocalEntries = () => {
	const serializedEntries = storage.getItem(ENTRIES_STORAGE_KEY);
	return serializedEntries ? JSON.parse(serializedEntries) : [];
};

export const localStorageSetThemePreference = (preference: ThemePreference) => {
	storage.setItem(THEME_MODE_KEY, preference);
};

export const localStorageGetThemePreference = (): ThemePreference => {
	const stored = storage.getItem(THEME_MODE_KEY);
	return Object.values(ThemePreference).includes(stored as ThemePreference) ? (stored as ThemePreference) : ThemePreference.Auto;
};

export const localStorageSetIsIntroDialogCompleted = (completed: boolean) => {
	storage.setItem(IS_INTRO_DIALOG_COMPLETED_KEY, completed.toString());
};

export const localStorageGetIsIntroDialogCompleted = (): boolean => {
	const storedIsIntroDialogCompleted = storage.getItem(IS_INTRO_DIALOG_COMPLETED_KEY);
	return storedIsIntroDialogCompleted === 'true';
};

export const localStorageSetIsAboutOnboardingCompleted = (completed: boolean) => {
	storage.setItem(IS_ABOUT_ONBOARDING_COMPLETED_KEY, completed.toString());
};

export const localStorageGetIsAboutOnboardingCompleted = (): boolean => {
	const storedIsAboutOnboardingCompleted = storage.getItem(IS_ABOUT_ONBOARDING_COMPLETED_KEY);
	return storedIsAboutOnboardingCompleted === 'true';
};

export const localStorageSetIsOfflineSyncPromptDismissed = (dismissed: boolean) => {
	storage.setItem(IS_OFFLINE_SYNC_PROMPT_DISMISSED_KEY, dismissed.toString());
};

export const localStorageGetIsOfflineSyncPromptDismissed = (): boolean => {
	return storage.getItem(IS_OFFLINE_SYNC_PROMPT_DISMISSED_KEY) === 'true';
};

export const sessionStorageSetIsAboutOnboardingCompleted = (completed: boolean) => {
	sessionStorageStore.setItem(IS_ABOUT_ONBOARDING_COMPLETED_KEY, completed.toString());
};

export const sessionStorageGetIsAboutOnboardingCompleted = (): boolean => {
	const storedIsAboutOnboardingCompleted = sessionStorageStore.getItem(IS_ABOUT_ONBOARDING_COMPLETED_KEY);
	return storedIsAboutOnboardingCompleted === 'true';
};

export const sessionStorageSetIsOfflineContinued = (continued: boolean) => {
	sessionStorageStore.setItem(IS_OFFLINE_CONTINUED_KEY, continued.toString());
};

export const sessionStorageGetIsOfflineContinued = (): boolean => {
	return sessionStorageStore.getItem(IS_OFFLINE_CONTINUED_KEY) === 'true';
};

const ENTRIES_TABLE_VISIBLE_PROPERTY_IDS_PREFIX = 'untwine.entriesTable.visiblePropertyIds.';

function entriesTableVisiblePropertyIdsKey(repositoryId: string) {
	return `${ENTRIES_TABLE_VISIBLE_PROPERTY_IDS_PREFIX}${repositoryId}`;
}

function parseVisiblePropertyIdsJson(raw: string): string[] | null {
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string')) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

/** `null` means all property columns (default). Otherwise ordered property definition ids to show. Persists across browser sessions. */
export function localStorageGetEntriesTableVisiblePropertyIds(repositoryId: string): string[] | null {
	const key = entriesTableVisiblePropertyIdsKey(repositoryId);
	try {
		const rawLocal = storage.getItem(key);
		if (rawLocal != null) {
			return parseVisiblePropertyIdsJson(rawLocal);
		}
		const rawSession = sessionStorageStore.getItem(key);
		if (rawSession == null) {
			return null;
		}
		const migrated = parseVisiblePropertyIdsJson(rawSession);
		sessionStorageStore.removeItem(key);
		if (migrated == null) {
			return null;
		}
		storage.setItem(key, JSON.stringify(migrated));
		return migrated;
	} catch {
		return null;
	}
}

export function localStorageSetEntriesTableVisiblePropertyIds(repositoryId: string, ids: string[] | null): void {
	const key = entriesTableVisiblePropertyIdsKey(repositoryId);
	if (ids == null || ids.length === 0) {
		storage.removeItem(key);
		sessionStorageStore.removeItem(key);
		return;
	}
	storage.setItem(key, JSON.stringify(ids));
	sessionStorageStore.removeItem(key);
}

/**
 * Reads cookie preferences from `cookiePreferencesV1`.
 * If only the legacy `isCookiePromptAccepted` key exists, migrates once to v1 and removes the legacy entry.
 * Returns `null` if the user has not completed the cookie UI yet.
 */
export function localStorageGetCookiePreferences(): CookiePreferencesV1 | null {
	const raw = storage.getItem(COOKIE_PREFERENCES_V1_STORAGE_KEY);
	if (raw) {
		try {
			const parsed = JSON.parse(raw) as Partial<CookiePreferencesV1>;
			if (typeof parsed.completed === 'boolean') {
				storage.removeItem(LEGACY_IS_COOKIE_PROMPT_ACCEPTED_KEY);
				return {
					completed: parsed.completed,
					analytics: typeof parsed.analytics === 'boolean' ? parsed.analytics : false,
					cookieChoiceConfirmedWithSave:
						typeof parsed.cookieChoiceConfirmedWithSave === 'boolean'
							? parsed.cookieChoiceConfirmedWithSave
							: undefined,
				};
			}
		} catch {
			// fall through to legacy migration
		}
	}

	const legacy = storage.getItem(LEGACY_IS_COOKIE_PROMPT_ACCEPTED_KEY);
	if (legacy !== null) {
		const migrated: CookiePreferencesV1 = {
			completed: true,
			analytics: legacy === 'true',
		};
		storage.setItem(COOKIE_PREFERENCES_V1_STORAGE_KEY, JSON.stringify(migrated));
		storage.removeItem(LEGACY_IS_COOKIE_PROMPT_ACCEPTED_KEY);
		return migrated;
	}

	return null;
}

export function localStorageSetCookiePreferences(prefs: CookiePreferencesV1) {
	storage.setItem(COOKIE_PREFERENCES_V1_STORAGE_KEY, JSON.stringify(prefs));
}

/** Clears v1 preferences and any legacy cookie prompt key (for reset / tests). */
export function clearCookiePreferenceStorage() {
	storage.removeItem(COOKIE_PREFERENCES_V1_STORAGE_KEY);
	storage.removeItem(LEGACY_IS_COOKIE_PROMPT_ACCEPTED_KEY);
}

const TIMELINE_RECORD_META_KEY = 'untwyn.timelineRecordMeta';

/**
 * Offline-mode counterpart to the `timeline_record_meta` table: repository entry id → y pixel
 * position, scoped by `parentId` (a Timeline element id, or a repositoryId for the ad hoc
 * collection-scoped timeline). Not wrapped as records — TimelineView reads/writes this directly.
 * All parents share one storage key, keyed by `parentId`.
 */
function readTimelineRecordMetaStore(): Record<string, Record<string, number>> {
	const raw = storage.getItem(TIMELINE_RECORD_META_KEY);
	return raw ? (JSON.parse(raw) as Record<string, Record<string, number>>) : {};
}

export function localStorageGetTimelineRecordMetaMap(parentId: string): Record<string, number> {
	return readTimelineRecordMetaStore()[parentId] ?? {};
}

export function localStorageSetTimelineRecordMetaMap(parentId: string, map: Record<string, number>): void {
	storage.setItem(TIMELINE_RECORD_META_KEY, JSON.stringify({ ...readTimelineRecordMetaStore(), [parentId]: map }));
}

export type CanvasViewport = { pan: { x: number; y: number }; scale: number };

/** All canvases share one storage key, keyed by parentId (`root` for the root canvas). */
const CANVAS_VIEWPORT_KEY = 'untwyn.canvasViewport';

function readCanvasViewportStore(): Record<string, CanvasViewport> {
	const raw = storage.getItem(CANVAS_VIEWPORT_KEY);
	return raw ? (JSON.parse(raw) as Record<string, CanvasViewport>) : {};
}

export function localStorageGetCanvasViewport(parentId: string | null): CanvasViewport | null {
	return readCanvasViewportStore()[parentId ?? 'root'] ?? null;
}

export function localStorageSetCanvasViewport(parentId: string | null, viewport: CanvasViewport): void {
	storage.setItem(CANVAS_VIEWPORT_KEY, JSON.stringify({ ...readCanvasViewportStore(), [parentId ?? 'root']: viewport }));
}

export type CanvasMeta = { isPositionLocked?: boolean };

/** All canvases share one storage key, keyed by parentId (`root` for the root canvas). */
const CANVAS_META_KEY = 'untwyn.canvasMeta';

function readCanvasMetaStore(): Record<string, CanvasMeta> {
	const raw = storage.getItem(CANVAS_META_KEY);
	return raw ? (JSON.parse(raw) as Record<string, CanvasMeta>) : {};
}

export function localStorageGetCanvasMeta(parentId: string | null): CanvasMeta {
	return readCanvasMetaStore()[parentId ?? 'root'] ?? {};
}

export function localStorageSetCanvasMeta(parentId: string | null, patch: CanvasMeta): void {
	const store = readCanvasMetaStore();
	const key = parentId ?? 'root';
	storage.setItem(CANVAS_META_KEY, JSON.stringify({ ...store, [key]: { ...store[key], ...patch } }));
}

export type TimelineMeta = { scale?: TimelineScale; isPositionLocked?: boolean };

/** All timelines share one storage key, keyed by the same `parentId` as `TIMELINE_RECORD_META_KEY`. */
const TIMELINE_META_KEY = 'untwyn.timelineMeta';

function readTimelineMetaStore(): Record<string, TimelineMeta> {
	const raw = storage.getItem(TIMELINE_META_KEY);
	return raw ? (JSON.parse(raw) as Record<string, TimelineMeta>) : {};
}

export function localStorageGetTimelineMeta(parentId: string): TimelineMeta {
	return readTimelineMetaStore()[parentId] ?? {};
}

export function localStorageSetTimelineMeta(parentId: string, patch: TimelineMeta): void {
	const store = readTimelineMetaStore();
	storage.setItem(TIMELINE_META_KEY, JSON.stringify({ ...store, [parentId]: { ...store[parentId], ...patch } }));
}
